import type { GreeningAnalysis } from '../../../services/greeningApi';
import { GREENING_ANALYSIS_QUESTIONS } from '../constants';
import {
  LIST_HEADER_PAUSE_MS,
  PAUSE_BULLET_MS,
  PAUSE_QUESTION_MS,
  PAUSE_SECTION_MS,
  SpeechSegment,
  cleanSpeechText,
  parseSpeechText,
  speechBreaths,
  speechPieces,
} from './browserSpeech';

function stamp(segments: SpeechSegment[], section: string): SpeechSegment[] {
  return segments.map((segment) => ({ ...segment, section }));
}

function pushSpoken(
  out: SpeechSegment[],
  text: string,
  pauseAfterMs: number,
  section: string,
  listHeader = false,
) {
  const pieces = speechPieces(cleanSpeechText(text)).filter((piece) => piece.text);
  pieces.forEach((piece, index) => {
    const last = index === pieces.length - 1;
    out.push({
      text: piece.text,
      pauseAfterMs: last ? pauseAfterMs : piece.pauseAfterMs,
      section,
      listHeader: listHeader && last ? true : undefined,
    });
  });
}

function appendList(out: SpeechSegment[], header: string, section: string, items?: string[]) {
  const clean = (items || []).map((item) => item.trim()).filter(Boolean);
  if (!clean.length) return;
  pushSpoken(out, header, LIST_HEADER_PAUSE_MS, section, true);
  clean.forEach((item, index) => {
    const pause = index === clean.length - 1 ? PAUSE_SECTION_MS : PAUSE_BULLET_MS;
    stamp(parseSpeechText(item, pause), section).forEach((segment) => {
      out.push({ ...segment, listItem: true });
    });
  });
}

/** Análise já gravada, em trechos com pausa. Não pede nada novo ao agente. */
export function analysisSpeechSegments(analysis?: GreeningAnalysis | null): SpeechSegment[] {
  if (!analysis) return [];
  const out: SpeechSegment[] = [];
  if (analysis.analysis_text?.trim()) {
    out.push(...stamp(parseSpeechText(analysis.analysis_text, PAUSE_SECTION_MS), 'analysis'));
  }
  appendList(out, 'Controles sugeridos', 'controls', analysis.controls);
  appendList(out, 'Demandas', 'demands', analysis.demands);
  appendList(out, 'Sanções e riscos', 'sanctions', analysis.sanctions);
  for (const item of GREENING_ANALYSIS_QUESTIONS) {
    const answer = analysis.five_whys?.[item.key]?.trim();
    if (!answer) continue;
    pushSpoken(out, item.label, PAUSE_QUESTION_MS, item.key);
    const parsed = parseSpeechText(answer, PAUSE_SECTION_MS);
    if (parsed.length) out.push(...stamp(parsed, item.key));
  }
  return out;
}

/** Frases da resposta, na ordem em que o chat as escreve e a voz as lê. */
export function answerSpeechLines(answer: string): string[] {
  return parseSpeechText(answer, 0).flatMap((segment) => speechBreaths(segment.text)).filter(Boolean);
}

/** Recusa já decidida pelo ask. A voz lê só este texto, nunca o pedido bloqueado. */
export function voiceGuardReply(payload: {
  status?: string;
  user_message?: string;
  answer?: string;
  scope_redirect?: boolean;
}): string | null {
  const status = payload.status || '';
  const message = (payload.user_message || payload.answer || '').trim();
  if (!message) return null;
  if (
    status === 'blocked_by_security' ||
    status === 'rate_limited' ||
    status === 'insufficient_context' ||
    payload.scope_redirect
  ) {
    return message;
  }
  return null;
}

export function spokenLinesAllowed(shown: number, total: number): number {
  if (total <= 0 || shown <= 0) return 0;
  if (total <= 2) return shown >= total ? total : 0;
  if (shown >= total) return total;
  return Math.max(0, shown - 2);
}

export function questionAndAnswerSegments(question: string, answer: string): SpeechSegment[] {
  const q = question.trim();
  const a = answer.trim();
  const out: SpeechSegment[] = [];
  if (q) pushSpoken(out, q, a ? PAUSE_QUESTION_MS : 0, 'question');
  if (a) out.push(...parseSpeechText(a, 0));
  return out;
}
